/**
 * SetupDock — Type B. The only persistent overlay.
 *
 * Bottom-right on every signed-in page. Steps are derived from real account
 * state, so completing one closes it; at 4/4 the dock retires for good.
 */

import React, { useEffect, useState } from 'react';
import { setSetupDockShown } from './setupDockShown';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useSetupDock, type SetupFacts } from './useOnboardingState';

interface Props {
  orgId: string;
  facts: SetupFacts;
  onNavigate: (route: string) => void;
  /** Hide on screens where a fixed dock would cover critical UI (e.g. canvas). */
  disabled?: boolean;
  /** Something is running: shrink to the header so the job dock above it stays visible. */
  busy?: boolean;
}

export const SetupDock: React.FC<Props> = ({
  orgId,
  facts,
  onNavigate,
  disabled = false,
  busy = false,
}) => {
  const dock = useSetupDock(orgId, facts);
  // While jobs run the dock stays shrunk unless the owner opens it; it goes back after.
  const [openedWhileBusy, setOpenedWhileBusy] = useState(false);
  useEffect(() => { if (!busy) setOpenedWhileBusy(false); }, [busy]);
  const shown = !disabled && dock.visible;
  useEffect(() => { setSetupDockShown(shown); return () => setSetupDockShown(false); }, [shown]);

  if (!shown) return null;
  const expanded = busy ? openedWhileBusy : dock.expanded;
  const toggle = () => (busy ? setOpenedWhileBusy(!openedWhileBusy) : dock.setExpanded(!dock.expanded));

  const pct = Math.round((dock.doneCount / dock.total) * 100);

  return (
    <div className="fixed bottom-4 right-4 z-[1030] w-[320px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl bg-navy shadow-2xl dark:bg-[#1A1D26] dark:ring-1">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2.5 border-b border-white/10 px-4 py-3 text-left hover:bg-card/5"
      >
        <span
          className="flex h-6 w-6 flex-none items-center justify-center rounded-full"
          style={{
            background: `conic-gradient(#F27C28 0 ${pct}%, rgba(255,255,255,.15) ${pct}% 100%)`,
          }}
        >
          <span className="h-[18px] w-[18px] rounded-full bg-navy dark:bg-[#1A1D26]" />
        </span>
        <span className="text-[13px] font-bold text-white">
          Get set up · {dock.doneCount} of {dock.total}
        </span>
        {expanded ? (
          <ChevronDown className="ml-auto h-4 w-4 text-white/45" />
        ) : (
          <ChevronUp className="ml-auto h-4 w-4 text-white/45" />
        )}
      </button>

      {expanded && (
        <>
          <ul className="py-1.5">
            {dock.steps.map((step) => (
              <li
                key={step.id}
                className={[
                  'flex items-start gap-2.5 px-4 py-2.5',
                  step.active ? 'border-l-2 border-primary bg-primary/10' : '',
                ].join(' ')}
              >
                <span
                  className={[
                    'mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full',
                    step.done
                      ? 'bg-[#0F9D6B]'
                      : step.active
                        ? 'border-[1.5px] border-primary'
                        : 'border-[1.5px] border-white/30',
                  ].join(' ')}
                >
                  {step.done && <Check className="h-2.5 w-2.5 text-white" />}
                </span>
                <div className="min-w-0">
                  <div
                    className={[
                      'text-[12.5px]',
                      step.done
                        ? 'text-white/50 line-through'
                        : step.active
                          ? 'font-bold text-white'
                          : 'text-white/85',
                    ].join(' ')}
                  >
                    {step.title}
                  </div>
                  {step.active && (
                    <>
                      <p className="mt-0.5 text-[11.5px] leading-relaxed text-white/60">
                        {step.body}
                      </p>
                      <button
                        type="button"
                        onClick={() => onNavigate(step.route)}
                        className="mt-2 rounded-md bg-primary px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-primaryHover"
                      >
                        {step.cta}
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={dock.snooze}
            className="w-full border-t border-white/10 px-4 py-2.5 text-left text-[11px] text-white/40 hover:text-white/70"
          >
            Hide until I'm ready
          </button>
        </>
      )}
    </div>
  );
};

export default SetupDock;
